/**
 * useSimpleValuationInput Hook
 * Handles API communication for simple valuation calculator input data
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const useSimpleValuationInput = (businessId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  /**
   * Get current user's auth token and ID
   */
  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not found');
    }

    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'X-User-Id': user.id,
      'X-Business-Id': businessId.toString()
    };
  };

  /**
   * Load simple valuation input data from the backend
   */
  const loadSimpleValuationInput = useCallback(async () => {
    if (!businessId) {
      logger.warn('No business ID provided for loading simple valuation input');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${API_URL}/simple-valuation-input/${businessId}`,
        { headers }
      );

      if (response.data.exists) {
        logger.info('Simple valuation input data loaded from backend', { businessId });
        setLastSaved(new Date(response.data.data.updated_at));
        return response.data.data;
      } else {
        logger.info('No simple valuation input data found for business', { businessId });
        return null;
      }
    } catch (err) {
      logger.error('Error loading simple valuation input data', { error: err, businessId });
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  /**
   * Save simple valuation input data to the backend
   */
  const saveSimpleValuationInput = useCallback(async (data) => {
    if (!businessId) {
      logger.warn('No business ID provided for saving simple valuation input');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${API_URL}/simple-valuation-input/${businessId}`,
        data,
        { headers }
      );

      if (response.data.success) {
        logger.info('Simple valuation input data saved successfully', { businessId });
        setLastSaved(new Date());
        return true;
      }

      return false;
    } catch (err) {
      logger.error('Error saving simple valuation input data', { error: err, businessId });
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  /**
   * Delete simple valuation input data from the backend
   */
  const deleteSimpleValuationInput = useCallback(async () => {
    if (!businessId) {
      logger.warn('No business ID provided for deleting simple valuation input');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await axios.delete(
        `${API_URL}/simple-valuation-input/${businessId}`,
        { headers }
      );

      if (response.data.success) {
        logger.info('Simple valuation input data deleted successfully', { businessId });
        setLastSaved(null);
        return true;
      }

      return false;
    } catch (err) {
      logger.error('Error deleting simple valuation input data', { error: err, businessId });
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  return {
    loadSimpleValuationInput,
    saveSimpleValuationInput,
    deleteSimpleValuationInput,
    loading,
    error,
    lastSaved
  };
};
